import { useEffect, useRef, useState } from "react";

export type TopBarScrollState = "full" | "toolbar" | "peek";

interface TopBarScrollResult {
  peekVisible: boolean;
  toolbarFixed: boolean;
}

export const useTopBarScrollState = (
  heroHeight: number,
  peekHeight = 65
): TopBarScrollResult => {
  const [result, setResult] = useState<TopBarScrollResult>({peekVisible: false,toolbarFixed: false});

  const lastYRef = useRef(0);
  const rafRef = useRef<number | null>(null);
  const upDistanceRef = useRef(0);
  const downDistanceRef = useRef(0);

  const resultRef = useRef(result);

  const setSafeResult = (next: TopBarScrollResult) => {
    const prev = resultRef.current;
    if (prev.peekVisible === next.peekVisible &&prev.toolbarFixed === next.toolbarFixed) return;

    resultRef.current = next;
    setResult(next);
  };

  useEffect(() => {
    if (window.innerWidth > 700) return;

    const update = () => {
      const y = window.scrollY || document.documentElement.scrollTop || 0;
      const dy = y - lastYRef.current;
      lastYRef.current = y;

      const toolbarFixThreshold = heroHeight;
      const normalReturnThreshold = heroHeight - peekHeight - 30;
      const peekReturnThreshold = heroHeight - 60;

      const isGoingUp = dy < -1;
      const isGoingDown = dy > 1;
      const isPeekVisible = resultRef.current.peekVisible;

      const heroReturnThreshold =
        isGoingUp && isPeekVisible
          ? peekReturnThreshold
          : normalReturnThreshold;

      const isBackInHeroSafeZone = y < heroReturnThreshold;

      const shouldKeepToolbarFixed =
        isPeekVisible && y >= heroReturnThreshold;

      const toolbarFixed = y >= toolbarFixThreshold || shouldKeepToolbarFixed;

      if (y < heroReturnThreshold) {
        upDistanceRef.current = 0;
        downDistanceRef.current = 0;

        setSafeResult({
          peekVisible: false,
          toolbarFixed,
        });

        return;
      }

      if (isGoingUp) {
        upDistanceRef.current += Math.abs(dy);
        downDistanceRef.current = 0;
      }

      if (isGoingDown) {
        downDistanceRef.current += dy;
        upDistanceRef.current = 0;
      }

      const canOpenPeek =
        resultRef.current.peekVisible || y > heroHeight + 200;

      const shouldShowPeek =
        toolbarFixed && canOpenPeek && upDistanceRef.current > 20;

      const shouldHidePeek =
        isBackInHeroSafeZone || downDistanceRef.current > 12;

      setSafeResult({
        peekVisible: shouldHidePeek
          ? false
          : shouldShowPeek || resultRef.current.peekVisible,
        toolbarFixed,
      });
    };

    lastYRef.current = window.scrollY || document.documentElement.scrollTop || 0;
    update();

    const onScroll = () => {
      if (rafRef.current !== null) return;

      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null;
        update();
      });
    };

    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", onScroll);
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, [heroHeight, peekHeight]);

  return result;
};